import { Helmet } from "react-helmet-async";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Privacy Policy | AI StoryCraft Studio</title>
        <meta
          name="description"
          content="Read how AI StoryCraft Studio collects, uses, and protects your data across authentication, billing, and product usage."
        />
        <link rel="canonical" href="https://ai-storycraft-studio.com/privacy-policy" />
        <meta property="og:title" content="Privacy Policy | AI StoryCraft Studio" />
        <meta
          property="og:description"
          content="Read how AI StoryCraft Studio collects, uses, and protects your data across authentication, billing, and product usage."
        />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://ai-storycraft-studio.com/privacy-policy" />
        <meta property="og:image" content="https://ai-storycraft-studio.com/mockups/mockup-nexis-ai.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Privacy Policy | AI StoryCraft Studio" />
        <meta
          name="twitter:description"
          content="Read how AI StoryCraft Studio collects, uses, and protects your data across authentication, billing, and product usage."
        />
        <meta name="twitter:image" content="https://ai-storycraft-studio.com/mockups/mockup-nexis-ai.png" />
      </Helmet>
      <Navbar />
      <main className="container mx-auto px-4 md:px-6 py-12 md:py-20 max-w-3xl">
        <h1 className="font-display text-3xl md:text-4xl font-bold mb-2">Privacy Policy</h1>
        <p className="text-muted-foreground mb-8">Last updated: March 2026</p>

        <div className="prose prose-invert max-w-none space-y-6 text-foreground">
          <section>
            <h2 className="font-display text-xl font-semibold mb-2">1. Introduction</h2>
            <p className="text-muted-foreground leading-relaxed">AI StoryCraft Studio ("we", "us", "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and share your personal data when you use our platform.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold mb-2">2. Data We Collect</h2>
            <p className="text-muted-foreground leading-relaxed">We collect the following information:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1 mt-2">
              <li>Account information: email address, name, and password (hashed)</li>
              <li>Payment information: processed securely by Stripe — we do not store card details</li>
              <li>Usage data: features used, credits consumed, content generated, and session activity</li>
              <li>Device data: browser type, IP address, and device identifiers for security purposes</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold mb-2">3. How We Use Your Data</h2>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1">
              <li>To provide and maintain the Platform and its features</li>
              <li>To process payments and manage your subscription</li>
              <li>To send transactional emails (welcome, onboarding, billing)</li>
              <li>To improve the Platform based on usage patterns</li>
              <li>To detect and prevent fraud and account abuse</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold mb-2">4. Third-Party Processors</h2>
            <p className="text-muted-foreground leading-relaxed">We use the following third-party services to operate the Platform:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1 mt-2">
              <li><strong>Stripe</strong> — payment processing and subscription management</li>
              <li><strong>Loops</strong> — transactional email delivery and contact management</li>
              <li><strong>Cloud infrastructure</strong> — database hosting, authentication, and file storage</li>
              <li><strong>AI model providers</strong> — to power script generation, product building, and other AI features (no personal data is sent to AI models)</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold mb-2">5. Data Retention</h2>
            <p className="text-muted-foreground leading-relaxed">We retain your data for as long as your account is active. If you delete your account, we will remove your personal data within 30 days, except where we are required to retain it by law (e.g., billing records).</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold mb-2">6. Your Rights</h2>
            <p className="text-muted-foreground leading-relaxed">Under GDPR and CCPA, you have the right to:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1 mt-2">
              <li>Access the personal data we hold about you</li>
              <li>Request correction of inaccurate data</li>
              <li>Request deletion of your data ("right to be forgotten")</li>
              <li>Object to or restrict processing of your data</li>
              <li>Request data portability</li>
              <li>Withdraw consent at any time</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-2">To exercise any of these rights, contact us at support@ai-storycraft-studio.com.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold mb-2">7. Cookies</h2>
            <p className="text-muted-foreground leading-relaxed">We use essential cookies for authentication and session management. We do not use third-party tracking cookies or advertising cookies.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold mb-2">8. Children's Privacy</h2>
            <p className="text-muted-foreground leading-relaxed">The Platform is not intended for users under the age of 16. We do not knowingly collect data from children.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold mb-2">9. Changes to This Policy</h2>
            <p className="text-muted-foreground leading-relaxed">We may update this Privacy Policy from time to time. We will notify you of significant changes via email or a notice on the Platform.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold mb-2">10. Contact</h2>
            <p className="text-muted-foreground leading-relaxed">For questions about this Privacy Policy or your data, contact us at support@ai-storycraft-studio.com.</p>
          </section>
        </div>
      </main>
      <FooterSection />
    </div>
  );
}
